/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useMemo, useEffect } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, collection } from 'firebase/firestore';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ComposedChart
} from 'recharts';
import {
  Settings,
  TrendingUp,
  Calendar,
  Plus,
  Trash2,
  Trophy,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Building,
  Target,
  Clock,
  Cloud,
  CloudOff,
  Copy,
  Check
} from 'lucide-react';

import appLogo from './assets/logo.png';

// --- Firebase Configuration ---
// When deploying, replace this placeholder with your actual Firebase config object
const firebaseConfig = {
  apiKey: "AIzaSyDGgBhq17x-fiNAcQvZBO9x8b2WHcl2_Es",
  authDomain: "mortgage-dashboard-3a7a5.firebaseapp.com",
  projectId: "mortgage-dashboard-3a7a5",
  storageBucket: "mortgage-dashboard-3a7a5.firebasestorage.app",
  messagingSenderId: "809629436687",
  appId: "1:809629436687:web:d765ff098c1fa61e378c7a",
  measurementId: "G-S539Q3LXBK"
};

// Use environment config if available (Canvas), otherwise use local config
const getFirebaseConfig = () => {
  if (typeof __firebase_config !== 'undefined') {
    return JSON.parse(__firebase_config);
  }
  return firebaseConfig;
};

// Prevent multiple initializations
const firebaseApp = getApps().length === 0 ? initializeApp(getFirebaseConfig()) : getApps()[0];
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

const getAppId = () => {
  if (typeof __app_id !== 'undefined') return __app_id;
  const params = new URLSearchParams(window.location.search);
  const existingId = params.get('id');
  if (existingId) return existingId;

  // Generate new ID if none exists
  const newId = doc(collection(db, 'artifacts')).id;
  const url = new URL(window.location.href);
  url.searchParams.set('id', newId);
  window.history.replaceState({}, '', url);
  return newId;
};

const appId = getAppId();
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.4';

// Robust formatting with fallback
const formatCurrency = (val, decimals = 0) => {
  if (!isFinite(val)) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  }).format(val);
};

// Formatter for inputs
const formatInputNumber = (val) => {
  if (val === "" || val === undefined || val === null) return "";
  const parts = val.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

// Parser for inputs
const parseInputNumber = (val) => {
  if (typeof val !== 'string') return val;
  return val.replace(/,/g, "");
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  if (isNaN(date.getTime())) return "Invalid Date";
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
};

const App = () => {
  const [user, setUser] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState(0);
  const [isConfigExpanded, setIsConfigExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedMilestones, setExpandedMilestones] = useState({
    'balanceReduction': true,
    'balanceThreshold': true,
    'equity': true,
    'payments': true
  });

  const [homes, setHomes] = useState([
    {
      id: 1,
      name: "Primary Residence",
      purchasePrice: 600000,
      loanAmount: 500000,
      downPayment: 100000,
      startDate: "2024-01-01",
      isARM: false,
      termMonths: 360,
      initialRate: 5.5,
      homeValuePoints: [{ date: "2024-01", value: 600000 }],
      rateChanges: [],
    }
  ]);

  // --- Auth Setup ---
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth error:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // --- Data Synchronization ---
  useEffect(() => {
    // Sync even if auth fails (rules are public)
    const docRef = doc(db, 'artifacts', appId, 'public', 'sharedConfig');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.homes) {
          setHomes(data.homes);
          setSyncStatus('synced');
        }
      }
    }, (err) => {
      console.error("Firestore sync error:", err);
      setSyncStatus('error');
    });
    return () => unsubscribe();
  }, [user]);

  const saveToCloud = async (updatedHomes) => {
    // Allow save even if auth fails
    setSyncStatus('syncing');
    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'sharedConfig');
      await setDoc(docRef, {
        homes: updatedHomes,
        lastUpdated: new Date().toISOString(),
        updatedBy: user?.uid || 'anonymous'
      });
      setSyncStatus('synced');
    } catch (err) {
      console.error("Save error:", err);
      setSyncStatus('error');
    }
  };

  const updateHome = (updates) => {
    const newHomes = [...homes];
    let updated = { ...newHomes[activeTab], ...updates };

    if (updates.purchasePrice !== undefined || updates.downPayment !== undefined) {
      const pp = updates.purchasePrice !== undefined ? updates.purchasePrice : (updated.purchasePrice ?? (updated.loanAmount + (updated.downPayment || 0)));
      const dp = updates.downPayment !== undefined ? updates.downPayment : (updated.downPayment || 0);
      updated.loanAmount = Math.max(0, pp - dp);
      updated.purchasePrice = pp;
      updated.downPayment = dp;

      if (updates.purchasePrice !== undefined) {
        if (!updated.homeValuePoints || updated.homeValuePoints.length === 0) {
          updated.homeValuePoints = [{ date: (updated.startDate || new Date().toISOString().split('T')[0]).slice(0, 7), value: pp }];
        } else {
          const sortedPoints = [...updated.homeValuePoints].sort((a, b) => a.date.localeCompare(b.date));
          const earliestDate = sortedPoints[0].date;
          updated.homeValuePoints = updated.homeValuePoints.map(p =>
            p.date === earliestDate ? { ...p, value: pp } : p
          );
        }
      }
    }

    newHomes[activeTab] = updated;
    setHomes(newHomes);
    saveToCloud(newHomes);
  };

  const addHome = () => {
    const startDate = new Date().toISOString().split('T')[0];
    const newHome = {
      id: Date.now(),
      name: "New Property",
      purchasePrice: 600000,
      loanAmount: 500000,
      downPayment: 100000,
      startDate: startDate,
      isARM: false,
      termMonths: 360,
      initialRate: 6.5,
      homeValuePoints: [{ date: startDate.slice(0, 7), value: 600000 }],
      rateChanges: [],
    };
    const newHomes = [...homes, newHome];
    setHomes(newHomes);
    setActiveTab(newHomes.length - 1);
    saveToCloud(newHomes);
  };

  const deleteHome = (e, index) => {
    e.stopPropagation(); // Prevent tab switching
    if (homes.length <= 1) return;

    // Confirmation dialog
    if (!window.confirm(`Are you sure you want to delete "${homes[index].name}"?`)) {
      return;
    }

    // If we're deleting the active tab, switch to the previous one (or first)
    if (activeTab === index) {
      setActiveTab(Math.max(0, index - 1));
    } else if (activeTab > index) {
      // If we're deleting a tab before the active one, shift index down
      setActiveTab(activeTab - 1);
    }

    const newHomes = homes.filter((_, i) => i !== index);
    setHomes(newHomes);
    saveToCloud(newHomes);
  };

  const currentHome = homes[activeTab];
  const today = useMemo(() => new Date(), []);

  // --- Amortization Logic ---
  const schedule = useMemo(() => {
    if (!currentHome.startDate || isNaN(new Date(currentHome.startDate).getTime())) {
      return [];
    }

    const data = [];
    let balance = currentHome.loanAmount;
    let totalInterestPaid = 0;
    let totalPrincipalPaid = 0;
    let totalMonthlyPayment = 0;

    const start = new Date(currentHome.startDate + 'T00:00:00');
    const sortedRateChanges = [...(currentHome.rateChanges || [])].sort((a, b) => a.date.localeCompare(b.date));
    const sortedValuePoints = [...(currentHome.homeValuePoints || [])].sort((a, b) => a.date.localeCompare(b.date));

    let currentRate = currentHome.initialRate;
    let currentMonthlyPayment = calculateMonthlyPayment(balance, currentRate, currentHome.termMonths);

    for (let i = 1; i <= currentHome.termMonths; i++) {
      const currentDate = new Date(start);
      currentDate.setMonth(start.getMonth() + i - 1);

      const year = currentDate.getFullYear();
      const monthNum = currentDate.getMonth() + 1;
      const monthStr = `${year}-${String(monthNum).padStart(2, '0')}`;
      const dateStr = `${monthStr}-01`;

      const change = sortedRateChanges.find(c => c.date === monthStr);
      if (change) {
        currentRate = change.rate;
        const remainingMonths = currentHome.termMonths - i + 1;
        currentMonthlyPayment = calculateMonthlyPayment(balance, currentRate, remainingMonths);
      }

      const mostRecentValue = sortedValuePoints.length > 0
        ? sortedValuePoints.reduce((prev, curr) => {
          return (curr.date <= monthStr) ? curr : prev;
        }, sortedValuePoints[0]).value
        : currentHome.loanAmount; // Fallback to loan amount if no value data

      const monthlyInterest = Number(((balance * (currentRate / 100)) / 12).toFixed(2));
      const principal = Number(Math.min(currentMonthlyPayment - monthlyInterest, balance).toFixed(2));

      balance = Number((balance - principal).toFixed(2));
      totalInterestPaid += monthlyInterest;
      totalPrincipalPaid += principal;
      totalMonthlyPayment += (monthlyInterest + principal);

      const equityValue = mostRecentValue - balance;
      const equityPercent = (equityValue / mostRecentValue) * 100;

      data.push({
        monthIndex: i,
        monthInYear: monthNum,
        dateLabel: formatDate(dateStr),
        year: year,
        date: dateStr,
        fullDate: new Date(currentDate),
        payment: currentMonthlyPayment,
        interest: monthlyInterest,
        principal: principal,
        balance: Math.max(0, balance),
        totalInterest: Number(totalInterestPaid.toFixed(2)),
        totalPrincipal: Number(totalPrincipalPaid.toFixed(2)),
        totalPaid: Number(totalMonthlyPayment.toFixed(2)),
        homeValue: mostRecentValue,
        equityValue,
        equityPercent,
        isCrossover: principal > monthlyInterest,
        rate: currentRate
      });

      if (balance <= 0) break;
    }
    return data;
  }, [currentHome]);

  const currentMonthData = useMemo(() => {
    if (!schedule.length) return null;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const match = schedule.find(m => m.year === currentYear && m.monthInYear === currentMonth);
    if (match) return match;
    return now < new Date(schedule[0].fullDate) ? schedule[0] : schedule[schedule.length - 1];
  }, [schedule]);

  const yearTicks = useMemo(() => schedule.filter(m => m.monthInYear === 1).map(m => m.monthIndex), [schedule]);

  function calculateMonthlyPayment(p, r, n) {
    if (r === 0) return Math.ceil((p / n) * 100) / 100;
    const i = r / 100 / 12;
    const payment = p * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
    return isFinite(payment) ? Number((Math.ceil(payment * 100) / 100).toFixed(2)) : 0;
  }

  const categorizedMilestones = useMemo(() => {
    const categories = {
      balanceReduction: { title: "Balance Reductions", icon: <TrendingUp className="w-4 h-4" />, items: [] },
      balanceThreshold: { title: "Balance Thresholds", icon: <Target className="w-4 h-4" />, items: [] },
      equity: { title: "Ownership & Equity", icon: <Building className="w-4 h-4" />, items: [] },
      payments: { title: "Payment Progress", icon: <CreditCard className="w-4 h-4" />, items: [] }
    };
    if (schedule.length === 0) return categories;
    let lastPaid100k = 0;
    let lastOwed100k = Math.ceil(currentHome.loanAmount / 100000) * 100000;
    let crossoverFound = false, principalOverInterestFound = false, halfOwnedFound = false;
    let lastEquityDecimal = 0;
    schedule.forEach((m) => {
      const milestoneDate = new Date(m.fullDate);
      const todayDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const isReached = milestoneDate <= todayDate;
      const reduction = currentHome.loanAmount - m.balance;
      if (Math.floor(reduction / 100000) > Math.floor(lastPaid100k / 100000)) {
        categories.balanceReduction.items.push({ label: `Total paid down by ${formatCurrency(Math.floor(reduction / 100000) * 100000)}`, dateLabel: m.dateLabel, isReached });
        lastPaid100k = reduction;
      }
      if (m.balance < lastOwed100k && lastOwed100k > 0) {
        categories.balanceThreshold.items.push({ label: `Balance dropped below ${formatCurrency(lastOwed100k)}`, dateLabel: m.dateLabel, isReached });
        lastOwed100k -= 100000;
      }

      let currentEquityDecimal = Math.floor(m.equityPercent / 10);
      if (currentEquityDecimal > lastEquityDecimal && currentEquityDecimal <= 10) {
        categories.equity.items.push({ label: `Reached ${currentEquityDecimal * 10}% home ownership`, dateLabel: m.dateLabel, isReached });
        lastEquityDecimal = currentEquityDecimal;
      }
      if (!crossoverFound && m.principal > m.interest) {
        categories.payments.items.push({ label: "Principal exceeds Interest in monthly payment", dateLabel: m.dateLabel, isReached });
        crossoverFound = true;
      }
      if (!principalOverInterestFound && m.totalPrincipal > m.totalInterest) {
        categories.payments.items.push({ label: "Total Principal paid exceeds Total Interest paid", dateLabel: m.dateLabel, isReached });
        principalOverInterestFound = true;
      }
    });
    return categories;
  }, [schedule, currentHome.loanAmount, today]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">

        {/* Cloud Sync Status */}
        <div className="mb-4 flex justify-end">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${syncStatus === 'synced' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
            syncStatus === 'syncing' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              'bg-slate-100 text-slate-500 border-slate-200'
            }`}>
            {syncStatus === 'synced' && <Cloud className="w-3 h-3" />}
            {syncStatus === 'syncing' && <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />}
            {syncStatus === 'error' && <CloudOff className="w-3 h-3" />}
            {syncStatus === 'synced' ? 'Cloud Synced' : syncStatus === 'syncing' ? 'Syncing...' : 'Disconnected'}
          </div>
        </div>

        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <img src={appLogo} alt="Mortgage Dashboard Logo" className="h-10 w-auto object-contain drop-shadow-sm" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-800">Mortgage Dashboard</h1>
              <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">v{APP_VERSION}</span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-slate-500 font-medium">Shared Property Data</p>
              <button
                onClick={() => {
                  // Ensure URL has the ID
                  const url = new URL(window.location.href);
                  url.searchParams.set('id', appId);
                  // Update history so the user sees it too if they haven't yet
                  window.history.pushState({}, '', url);

                  try {
                    navigator.clipboard.writeText(url.toString());
                  } catch (e) {
                    console.error('Clipboard failed', e);
                  }
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="flex items-center gap-1.5 text-[10px] uppercase font-bold bg-white border border-slate-200 px-2 py-1 rounded hover:bg-slate-50 transition-all text-slate-400 hover:text-slate-600"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied Link!' : `Share Link (${appId.slice(0, 8)}...)`}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white rounded-xl p-1 shadow-sm border border-slate-200 overflow-x-auto max-w-full">
              {homes.map((home, idx) => (
                <button
                  key={home.id}
                  onClick={() => setActiveTab(idx)}
                  className={`group relative px-4 pr-8 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === idx
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                  {home.name}
                  {homes.length > 1 && (
                    <span
                      onClick={(e) => deleteHome(e, idx)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${activeTab === idx ? "hover:bg-blue-700 text-blue-200" : "hover:bg-slate-200 text-slate-400"
                        }`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={addHome}
              className="p-2 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 hover:text-blue-600 transition-colors shadow-sm"
              aria-label="Add New Property"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4 space-y-6">
            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <button
                onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                className="w-full flex items-center justify-between p-6 border-b hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2 text-slate-700">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <h2 className="font-semibold text-lg">Loan Configuration</h2>
                </div>
                {isConfigExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>

              {isConfigExpanded && (
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Property Name</label>
                    <input
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500 transition-colors"
                      value={currentHome.name}
                      onChange={e => updateHome({ name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Purchase Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-slate-400">$</span>
                      <input
                        type="text"
                        className="w-full pl-7 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                        value={formatInputNumber(currentHome.purchasePrice ?? (currentHome.loanAmount + (currentHome.downPayment || 0)))}
                        onChange={e => {
                          const val = parseInputNumber(e.target.value);
                          if (!isNaN(val) || val === "") {
                            updateHome({ purchasePrice: val === "" ? 0 : Number(val) });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Downpayment</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-slate-400">$</span>
                      <input
                        type="text"
                        className="w-full pl-7 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                        value={formatInputNumber(currentHome.downPayment || 0)}
                        onChange={e => {
                          const val = parseInputNumber(e.target.value);
                          if (!isNaN(val) || val === "") {
                            updateHome({ downPayment: val === "" ? 0 : Number(val) });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-slate-400">$</span>
                      <input
                        type="text"
                        disabled
                        className="w-full pl-7 pr-4 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-500 cursor-not-allowed"
                        value={formatInputNumber(currentHome.loanAmount)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Start Date</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                      value={currentHome.startDate}
                      onChange={e => updateHome({ startDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Initial Rate (%)</label>
                    <input
                      type="number" step="0.001"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                      value={currentHome.initialRate}
                      onChange={e => updateHome({ initialRate: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wider">Term (Months)</label>
                    <input
                      type="number"
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                      value={currentHome.termMonths}
                      onChange={e => updateHome({ termMonths: Number(e.target.value) })}
                    />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer pt-2 select-none group">
                    <input type="checkbox" checked={currentHome.isARM} onChange={e => updateHome({ isARM: e.target.checked })} className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500" />
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">Adjustable Rate Mortgage (ARM)</span>
                  </label>

                  {currentHome.isARM && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">Rate Adjustments</h3>
                        <button onClick={() => updateHome({ rateChanges: [...(currentHome.rateChanges || []), { date: '2026-03', rate: currentHome.initialRate }] })} className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors shadow-sm"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      {(currentHome.rateChanges || []).map((change, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input type="month" className="text-xs p-2 rounded border border-blue-200 w-full focus:ring-1 focus:ring-blue-500 outline-none" value={change.date} onChange={e => {
                            const newRates = [...currentHome.rateChanges];
                            newRates[idx] = { ...newRates[idx], date: e.target.value };
                            updateHome({ rateChanges: newRates });
                          }} />
                          <input type="number" step="0.125" className="text-xs p-2 rounded border border-blue-200 w-24 focus:ring-1 focus:ring-blue-500 outline-none" value={change.rate} onChange={e => {
                            const newRates = [...currentHome.rateChanges];
                            newRates[idx] = { ...newRates[idx], rate: Number(e.target.value) };
                            updateHome({ rateChanges: newRates });
                          }} />
                          <button onClick={() => updateHome({ rateChanges: currentHome.rateChanges.filter((_, i) => i !== idx) })} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Property Values</h3>
                      <button onClick={() => updateHome({ homeValuePoints: [...(currentHome.homeValuePoints || []), { date: '2026-01', value: 1500000 }] })} className="p-1.5 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    {(currentHome.homeValuePoints || []).map((point, idx) => (
                      <div key={idx} className="flex gap-2 mb-2">
                        <input type="month" className="text-xs p-2 rounded border border-slate-200 flex-1 focus:ring-1 focus:ring-slate-400 outline-none" value={point.date} onChange={e => {
                          const newPoints = [...currentHome.homeValuePoints];
                          newPoints[idx] = { ...newPoints[idx], date: e.target.value };
                          updateHome({ homeValuePoints: newPoints });
                        }} />
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-2 text-[10px] text-slate-400">$</span>
                          <input
                            type="text"
                            className="text-xs p-2 pl-4 rounded border border-slate-200 w-full focus:ring-1 focus:ring-slate-400 outline-none"
                            value={formatInputNumber(point.value)}
                            onChange={e => {
                              const val = parseInputNumber(e.target.value);
                              if (!isNaN(val) || val === "") {
                                const newPoints = [...currentHome.homeValuePoints];
                                newPoints[idx] = { ...newPoints[idx], value: val === "" ? 0 : Number(val) };
                                updateHome({ homeValuePoints: newPoints });
                              }
                            }}
                          />
                        </div>
                        <button onClick={() => updateHome({ homeValuePoints: currentHome.homeValuePoints.filter((_, i) => i !== idx) })} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl">
              <h3 className="text-slate-400 text-xs font-bold uppercase mb-4 tracking-widest text-center">Calculated Summary</h3>
              <div className="space-y-4">
                {/* Loan & Equity */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 p-2.5 rounded-xl border border-slate-700/50">
                    <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">Initial Balance</p>
                    <p className="text-base font-semibold text-slate-200 tracking-tight">{formatCurrency(currentHome.loanAmount)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-2.5 rounded-xl border border-slate-700/50">
                    <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">Remaining Balance</p>
                    <p className="text-base font-semibold text-red-300 tracking-tight">{formatCurrency(currentMonthData?.balance || 0)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-2.5 rounded-xl border border-slate-700/50">
                    <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">Amount Owned</p>
                    <p className="text-base font-semibold text-emerald-400 tracking-tight">{formatCurrency(currentMonthData?.equityValue || 0)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-2.5 rounded-xl border border-slate-700/50">
                    <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">% Owned</p>
                    <p className="text-base font-semibold text-emerald-400 tracking-tight">{Math.round(currentMonthData?.equityPercent || 0)}%</p>
                  </div>
                </div>

                <div className="border-t border-slate-800" />

                {/* Monthly Payment */}
                <div className="bg-blue-600/10 p-3 rounded-xl border border-blue-500/20 text-center">
                  <p className="text-blue-200 text-[10px] uppercase font-bold mb-1">Current Monthly Payment</p>
                  <p className="text-2xl font-bold text-white tracking-tight">{formatCurrency(currentMonthData?.payment || 0, 2)}</p>
                </div>

                {/* Cumulative Stats */}
                <div className="space-y-3 pt-2">
                  <div>
                    <div className="flex justify-between items-end mb-1">
                      <p className="text-slate-400 text-[10px] uppercase font-bold">Payments (Principal + Interest)</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="block text-slate-500 text-[10px]">Paid So Far</span>
                        <span className="font-semibold text-white">{formatCurrency(currentMonthData?.totalPaid || 0)}</span>
                      </div>
                      <div className="text-right">
                        <span className="block text-slate-500 text-[10px]">Lifetime Total</span>
                        <span className="font-semibold text-white">{formatCurrency(schedule[schedule.length - 1]?.totalPaid || 0)}</span>
                      </div>
                    </div>
                    <div className="w-full h-1 bg-slate-800 rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${Math.min(100, ((currentMonthData?.totalPaid || 0) / (schedule[schedule.length - 1]?.totalPaid || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-end mb-1">
                      <p className="text-slate-400 text-[10px] uppercase font-bold">Interest Only</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="block text-slate-500 text-[10px]">Paid So Far</span>
                        <span className="font-semibold text-orange-300">{formatCurrency(currentMonthData?.totalInterest || 0)}</span>
                      </div>
                      <div className="text-right">
                        <span className="block text-slate-500 text-[10px]">Lifetime Total</span>
                        <span className="font-semibold text-orange-400">{formatCurrency(schedule[schedule.length - 1]?.totalInterest || 0)}</span>
                      </div>
                    </div>
                    <div className="w-full h-1 bg-slate-800 rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full bg-orange-500"
                        style={{ width: `${Math.min(100, ((currentMonthData?.totalInterest || 0) / (schedule[schedule.length - 1]?.totalInterest || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </aside>

          <main className="lg:col-span-8 space-y-8">
            {schedule.length === 0 ? (
              <div className="bg-white p-16 rounded-3xl text-center border-2 border-dashed border-slate-200">
                <div className="bg-slate-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="text-slate-400 w-6 h-6" />
                </div>
                <p className="text-slate-500 font-semibold">Please enter a valid loan start date to generate data.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8">
                {/* Visualizations */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                  <h3 className="font-bold mb-6 text-slate-800 flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-blue-500 rounded-full" />
                    Monthly Payment Composition
                  </h3>
                  <div className="h-64 w-full">
                    <ResponsiveContainer>
                      <BarChart data={schedule}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="monthIndex"
                          ticks={yearTicks}
                          tickFormatter={(val) => {
                            const entry = schedule.find(d => d.monthIndex === val);
                            return entry ? entry.year : '';
                          }}
                          minTickGap={60}
                          fontSize={10}
                          tick={{ fill: '#64748b' }}
                          axisLine={{ stroke: '#e2e8f0' }}
                          tickLine={{ stroke: '#e2e8f0' }}
                          padding={{ left: 10, right: 10 }}
                        />
                        <YAxis tickFormatter={(val) => formatCurrency(val)} width={80} fontSize={10} tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip
                          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-100">
                                  <p className="font-bold text-slate-800 mb-2">{data.dateLabel}</p>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                                      <span className="text-slate-500">Principal:</span>
                                      <span className="font-mono font-medium ml-auto font-bold text-slate-700">{formatCurrency(data.principal, 2)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                                      <span className="text-slate-500">Interest:</span>
                                      <span className="font-mono font-medium ml-auto text-slate-600">{formatCurrency(data.interest, 2)}</span>
                                    </div>
                                    <div className="my-1 border-t border-slate-100"></div>
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                                      <span>Total:</span>
                                      <span className="font-mono ml-auto">{formatCurrency(data.payment, 2)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                        <Bar dataKey="principal" name="Principal" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="interest" name="Interest" stackId="a" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                  <h3 className="font-bold mb-6 text-slate-800 flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-orange-500 rounded-full" />
                    Principal vs. Interest Over Time
                  </h3>
                  <div className="h-72 w-full">
                    <ResponsiveContainer>
                      <LineChart data={schedule} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="monthIndex"
                          ticks={yearTicks}
                          tickFormatter={(val) => {
                            const entry = schedule.find(d => d.monthIndex === val);
                            return entry ? entry.year : '';
                          }}
                          minTickGap={60}
                          fontSize={10}
                          tick={{ fill: '#64748b' }}
                          axisLine={{ stroke: '#e2e8f0' }}
                          tickLine={{ stroke: '#e2e8f0' }}
                          padding={{ left: 10, right: 10 }}
                        />
                        <YAxis tickFormatter={(val) => formatCurrency(val)} width={80} fontSize={10} tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <Tooltip
                          labelFormatter={(val) => {
                            const entry = schedule.find(d => d.monthIndex === val);
                            return entry ? entry.dateLabel : '';
                          }}
                          formatter={(val) => formatCurrency(val, 2)}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        />
                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                        <Line type="monotone" dataKey="principal" name="Principal" stroke="#2563eb" strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="interest" name="Interest" stroke="#f59e0b" strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                  <h3 className="font-bold mb-6 text-slate-800 flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                    Loan Progress & Equity
                  </h3>
                  <div className="h-80 w-full">
                    <ResponsiveContainer>
                      <ComposedChart data={schedule} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="monthIndex"
                          ticks={yearTicks}
                          tickFormatter={(val) => {
                            const entry = schedule.find(d => d.monthIndex === val);
                            return entry ? entry.year : '';
                          }}
                          minTickGap={60}
                          fontSize={10}
                          tick={{ fill: '#64748b' }}
                          axisLine={{ stroke: '#e2e8f0' }}
                          tickLine={{ stroke: '#e2e8f0' }}
                          padding={{ left: 10, right: 10 }}
                        />
                        <YAxis tickFormatter={(val) => formatCurrency(val)} width={90} fontSize={10} tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <Tooltip
                          labelFormatter={(val) => {
                            const entry = schedule.find(d => d.monthIndex === val);
                            return entry ? entry.dateLabel : '';
                          }}
                          formatter={(val) => formatCurrency(val)}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        />
                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                        <Line type="monotone" dataKey="balance" name="Loan Balance" stroke="#2563eb" strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="totalPaid" name="Total Paid" stroke="#10b981" dot={false} strokeWidth={2.5} />
                        <Line type="monotone" dataKey="totalPrincipal" name="Principal Paid" stroke="#f97316" dot={false} strokeWidth={2.5} strokeDasharray="5 5" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Milestones Sections */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <h3 className="font-bold text-slate-800">Financial Milestones</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {Object.entries(categorizedMilestones).map(([key, cat]) => (
                  <div key={key}>
                    <button onClick={() => setExpandedMilestones(p => ({ ...p, [key]: !p[key] }))} className="w-full flex items-center justify-between p-4 px-6 hover:bg-slate-50 transition-colors group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg text-slate-600 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">{cat.icon}</div>
                        <span className="font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">{cat.title} ({cat.items.length})</span>
                      </div>
                      {expandedMilestones[key] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {expandedMilestones[key] && (
                      <div className="px-6 pb-4 space-y-2 bg-slate-50/30">
                        {cat.items.length === 0 ? <p className="text-xs text-slate-400 italic py-3">No milestones reached yet in this category.</p> : cat.items.map((item, i) => (
                          <div key={i} className={`flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 ${!item.isReached ? 'opacity-60' : ''}`}>
                            <div className="flex items-center gap-3">
                              {item.isReached ? (
                                <span className="text-lg leading-none" role="img" aria-label="reached">✅</span>
                              ) : (
                                <Clock className="w-4 h-4 text-slate-400" />
                              )}
                              <span className={`text-sm ${item.isReached ? 'text-slate-700 font-medium' : 'text-slate-500 italic'}`}>{item.label}</span>
                            </div>
                            <span className={`text-[10px] font-bold border px-2 py-0.5 rounded uppercase tracking-wider ${item.isReached ? 'text-blue-600 bg-blue-50 border-blue-100' : 'text-slate-400 bg-white border-slate-100'}`}>
                              {item.dateLabel}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Amortization Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100"><h3 className="font-bold flex items-center gap-2 text-slate-800"><Calendar className="w-4 h-4 text-purple-500" />Full Amortization Schedule</h3></div>
              <div className="overflow-x-auto max-h-[600px]">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 bg-slate-50 z-10 shadow-sm">
                    <tr className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                      <th className="px-6 py-4">#</th>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Rate</th>
                      <th className="px-6 py-4">Payment</th>
                      <th className="px-6 py-4">Principal / Interest</th>
                      <th className="px-6 py-4">Balance</th>
                      <th className="px-6 py-4">Equity (%)</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs divide-y divide-slate-100">
                    {schedule.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 group">
                        <td className="px-6 py-4 text-slate-400 font-mono text-[10px]">{row.monthIndex}</td>
                        <td className="px-6 py-4 font-semibold text-slate-700">{row.dateLabel}</td>
                        <td className="px-6 py-4"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold border border-blue-100">{row.rate.toFixed(3)}%</span></td>
                        <td className="px-6 py-4 font-bold text-slate-900">{formatCurrency(row.payment, 2)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="text-blue-600 font-bold">{formatCurrency(row.principal, 2)}</span>
                            <span className="text-slate-400 font-medium">{formatCurrency(row.interest, 2)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-600">{formatCurrency(row.balance)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-800 min-w-[30px]">{Math.round(row.equityPercent)}%</span>
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${row.equityPercent}%` }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default App;
